<template>
  <NuxtLayout name="auth">
    <div class="fixed inset-0 z-0 overflow-hidden pointer-events-none">
      <div
        class="absolute top-[-10%] right-[-10%] w-[40%] h-[40%] bg-brand/10 blur-[120px] rounded-full"
      />
      <div
        class="absolute bottom-[-10%] left-[-10%] w-[40%] h-[40%] bg-brand/5 blur-[120px] rounded-full"
      />
    </div>

    <div class="relative z-10 w-full max-w-lg">
      <div class="flex flex-col items-center mb-10 text-center">
        <PebbleAppHomeLogo class="h-10 mb-6" />
        <h1 class="text-4xl font-black text-white tracking-tighter mb-2">
          Create <span class="text-brand">Account</span>
        </h1>
        <p class="text-text-weak-dark text-[10px] font-black tracking-[0.3em]">
          Step {{ currentStep }} of 3:
          {{ registrationSteps[currentStep - 1]!.title }}
        </p>
      </div>

      <div
        class="glass-heavy border border-white/10 rounded-[2.5rem] p-8 lg:p-12 shadow-2xl relative overflow-hidden"
      >
        <div
          class="absolute top-0 inset-x-0 h-px bg-linear-to-r from-transparent via-white/20 to-transparent"
        />

        <div class="mb-10">
          <PebbleAppStepper
            v-if="registrationSteps?.length"
            :current-step="currentStep"
            :steps="registrationSteps"
            variant="compact"
          />
        </div>

        <div v-if="currentStep === 1" class="space-y-8">
          <form @submit.prevent="nextStep" class="space-y-6">
            <PebbleAppFormInput
              v-model="name"
              label="Full name"
              placeholder="John Doe"
              :error="nameError"
            >
              <template #leftIcon><User class="size-4 opacity-40" /></template>
            </PebbleAppFormInput>

            <PebbleAppFormInput
              v-model="mobile"
              v-model:country-code="selectedCountryCode"
              label="Phone number"
              placeholder="000 000 000"
              is-mobile-field
              :error="mobileError"
            />

            <PebbleAppFormInput
              v-model="email"
              label="Email address"
              placeholder="user@example.com"
              type="email"
              :error="emailError"
            >
              <template #leftIcon><Mail class="size-4 opacity-40" /></template>
            </PebbleAppFormInput>

            <PebbleAppLink
              type="submit"
              variant="primary"
              size="lg"
              :trailing-icon="ArrowRight"
              :full-width="true"
            >
              Continue
            </PebbleAppLink>
          </form>

          <div class="space-y-6">
            <div class="relative flex items-center justify-center">
              <span class="w-full border-t border-white/5" />
              <span
                class="absolute px-4 bg-[#0f0f11] text-[10px] font-black text-white/30 tracking-[0.3em]"
              >
                Quick sign up
              </span>
            </div>

            <PebbleAppSocialAuthentication />
          </div>
        </div>

        <form
          v-if="currentStep === 2"
          class="space-y-6"
          @submit.prevent="handleSubmitRegistration"
        >
          <PebbleAppFormInput
            v-model="password"
            label="Password"
            placeholder="••••••••"
            is-password-field
            :error="passwordError"
          >
            <template #leftIcon><Lock class="size-4 opacity-40" /></template>
          </PebbleAppFormInput>

          <PebbleAppFormInput
            v-model="passwordConfirmation"
            label="Confirm password"
            placeholder="••••••••"
            is-password-field
            :error="passwordConfirmationError"
          >
            <template #leftIcon
              ><ShieldCheck class="size-4 opacity-40"
            /></template>
          </PebbleAppFormInput>

          <div class="flex flex-col gap-4 pt-4">
            <PebbleAppLink
              type="submit"
              variant="primary"
              size="lg"
              :leading-icon="UserPlus"
              :full-width="true"
              :loading="isLoading"
            >
              Create account
            </PebbleAppLink>

            <button
              @click="currentStep--"
              type="button"
              class="text-[10px] font-black text-white/40 hover:text-brand tracking-widest transition-colors flex items-center justify-center gap-2"
            >
              <ArrowLeft class="size-3" /> Go back
            </button>
          </div>
        </form>

        <div v-if="currentStep === 3" class="text-center">
          <div class="relative w-20 h-20 mx-auto mb-8">
            <div
              class="absolute inset-0 bg-brand/20 blur-xl rounded-full animate-pulse"
            />
            <div
              class="relative w-full h-full glass-premium rounded-2xl flex items-center justify-center border border-brand/30 shadow-[0_0_20px_rgba(255,95,0,0.2)]"
            >
              <MailCheck class="text-brand w-10 h-10" />
            </div>
          </div>

          <h2 class="text-2xl font-black text-white tracking-tighter mb-2">
            Check your email
          </h2>
          <p
            class="text-text-weak-dark text-[11px] font-bold tracking-widest mb-8 leading-loose"
          >
            We sent a 6-digit code to<br />
            <span
              class="text-white bg-white/5 px-2 py-1 rounded-lg border border-white/10"
              >{{ registrationEmail }}</span
            >
          </p>

          <div class="flex justify-center mb-10">
            <PebbleAppFormInputOTP
              v-model="otpCode"
              :length="6"
              @complete="verifyOtp"
            />
          </div>

          <PebbleAppLink
            variant="primary"
            size="lg"
            :full-width="true"
            :disabled="otpCode.length < 6 || isLoading"
            :loading="isLoading"
          >
            Verify account
          </PebbleAppLink>
          <p
            class="text-[10px] font-black text-text-weak-dark tracking-widest mt-5"
          >
            Didn’t receive it?
            <button
              @click="handleResendOtp"
              class="text-brand hover:text-orange-400 transition-colors ml-1 underline underline-offset-4 decoration-brand/30 cursor-pointer"
            >
              Send again
            </button>
          </p>
        </div>
      </div>

      <p
        v-if="currentStep < 3"
        class="mt-10 text-center text-xs font-bold text-text-weak-dark tracking-widest"
      >
        Already have an account?
        <NuxtLink
          to="/login"
          class="text-brand hover:text-orange-400 transition-colors ml-2"
        >
          Sign in
        </NuxtLink>
      </p>
    </div>
  </NuxtLayout>
</template>

<style scoped>
.glass-heavy {
  background: rgba(15, 15, 17, 0.85);
  backdrop-filter: blur(28px) saturate(180%);
  -webkit-backdrop-filter: blur(28px) saturate(180%);
}

.glass-premium {
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(12px);
}
</style>

<script lang="ts" setup>
import { ref, markRaw } from "vue";
import {
  User,
  UserPlus,
  Mail,
  Lock,
  ShieldCheck,
  ArrowLeft,
  MailCheck,
  Fingerprint,
  ArrowRight,
} from "lucide-vue-next";
import {
  signupStepOneSchema,
  signupStepTwoSchema,
} from "@/utils/validations/signup";
import { useNuxtServerApi } from "@/composables/http/useNuxtServerAPI";
import { usePhoneUtils } from "@/composables/usePhoneUtils";

usePageTitle({
  title: "Join Pebble",
  description: "Create your Pebble account",
});

const $nuxtApi = useNuxtServerApi();

const registrationEmail = useCookie<string | null>("reg_email", {
  maxAge: 3600,
});
const registrationStep = useCookie<number>("reg_step", { maxAge: 3600 });

const currentStep = ref(Number(registrationStep.value) || 1);
const otpCode = ref("");
const selectedCountryCode = ref("+233");
const isLoading = ref(false);
const isResendingOtp = ref(false);
const { showErrorToast, showSuccessToast } = useNotification();
const { removeLeadingZeros, formatWithCountryCode } = usePhoneUtils();
const { autoAuthenticateUser } = useAuth();

const route = useRoute();
registrationEmail.value = (route.query.email as string) || null;
registrationStep.value = Number(route.query.step) || 1;

const registrationSteps = [
  { step: 1, title: "Identity", icon: markRaw(User) },
  { step: 2, title: "Security", icon: markRaw(Fingerprint) },
  { step: 3, title: "Verify", icon: markRaw(MailCheck) },
];

const stepOneSchema = signupStepOneSchema(selectedCountryCode);
const stepTwoSchema = signupStepTwoSchema();

const stepOneForm = useForm({
  validationSchema: stepOneSchema,
  initialValues: {
    name: "",
    email: "",
    mobile: "",
  },
});
const { resetForm: resetStepOneForm, handleSubmit: handleSubmitStepOne } =
  stepOneForm;

const stepTwoForm = useForm({
  validationSchema: stepTwoSchema,
  initialValues: {
    password: "",
    password_confirmation: "",
  },
});
const { resetForm: resetStepTwoForm, handleSubmit: handleSubmitStepTwo } =
  stepTwoForm;

const { value: name, errorMessage: nameError } = useField<string>(
  "name",
  undefined,
  { form: stepOneForm },
);
const { value: email, errorMessage: emailError } = useField<string>(
  "email",
  registrationEmail.value || "",
  { form: stepOneForm },
);
const { value: mobile, errorMessage: mobileError } = useField<string>(
  "mobile",
  undefined,
  { form: stepOneForm },
);
const { value: password, errorMessage: passwordError } = useField<string>(
  "password",
  undefined,
  { form: stepTwoForm },
);
const { value: passwordConfirmation, errorMessage: passwordConfirmationError } =
  useField<string>("password_confirmation", undefined, { form: stepTwoForm });

watch(mobile, (newVal: string) => {
  if (newVal.startsWith("0")) {
    mobile.value = removeLeadingZeros(newVal);
  }
});

watch(currentStep, (newStep) => {
  registrationStep.value = newStep;
});

watch(email, (newEmail) => {
  registrationEmail.value = newEmail;
  stepOneForm.setFieldValue("email", newEmail);
});

const nextStep = async () => {
  if (currentStep.value === 1) {
    const { valid } = await stepOneForm.validate();
    if (valid) currentStep.value++;
  } else if (currentStep.value === 2) {
    const { valid } = await stepTwoForm.validate();
    if (valid) currentStep.value++;
  } else {
    currentStep.value++;
  }
};

const handleSubmitRegistration = async () => {
  const { valid } = await stepTwoForm.validate();
  if (!valid) return;

  isLoading.value = true;

  const payload = {
    name: name.value,
    email: email.value,
    mobile: formatWithCountryCode(mobile.value, selectedCountryCode.value),
    password: password.value,
    password_confirmation: passwordConfirmation.value,
  };

  try {
    const response: ClientSideApiResponse = await $nuxtApi.post(
      "/auth/get-started",
      payload,
    );
    if (statusOk(response.status)) {
      registrationEmail.value = email.value;
      registrationStep.value = 3;
      showSuccessToast(
        "Success",
        "Account successfully created. An email has been sent to you.",
      );
      currentStep.value = 3;
    }
  } catch (error) {
    console.log("Registration error:", error);
  } finally {
    isLoading.value = false;
  }
};

const resetForm = () => {
  resetStepOneForm();
  resetStepTwoForm();
  registrationEmail.value = null;
  registrationStep.value = 1;
  otpCode.value = "";
  currentStep.value = 1;
};

const verifyOtp = async () => {
  if (isLoading.value) return;
  const payload = {
    otp: otpCode.value,
    identifier: registrationEmail.value,
  };

  isLoading.value = true;

  try {
    const response = await autoAuthenticateUser(
      payload,
      "auth/otp-verification",
      "/profiles",
    );
    if (statusOk(response.status)) {
      resetForm();
      showSuccessToast(
        "Success",
        "Account successfully verified. Welcome to Pebble.",
      );
    }
  } catch (error: any) {
    showErrorToast(
      "Verification failed",
      error?.message || "Please try again.",
    );
  } finally {
    isLoading.value = false;
  }
};

async function handleResendOtp(): Promise<void> {
  if (isLoading.value || isResendingOtp.value) return;

  const verificationIdentifier = registrationEmail.value;

  if (!verificationIdentifier) {
    showErrorToast(
      "Session Expired",
      "Registration identifier missing. Please start over.",
    );
    return;
  }

  isResendingOtp.value = true;

  try {
    const response = await $nuxtApi.post<ClientSideApiResponse>(
      `auth/resend-otp/${verificationIdentifier}`,
      {},
    );
    console.log(response)

    if (statusOk(response.status)) {
      otpCode.value = "";
      showSuccessToast(
        "OTP Sent",
        "A new verification code has been sent to your email.",
      );
    }
  } catch (error: unknown) {
    handleError(error, "resending OTP");
  } finally {
    isResendingOtp.value = false;
  }
}
</script>
